import { z } from 'zod';

import { ClientSideGridResponseSchema } from '@/components/AgGrid/schemas';

// ============================================================================
// NAMING CONVENTIONS
// ============================================================================
// Tier 1 — Enums:    `<Name>Schema` + type `<Name>`          — both exported
// Tier 2 — Entities: `<Domain><Name>Schema` + type `<Domain><Name>` — both exported
// Tier 3 — Per-endpoint schemas (Body / Path / Query / Response):
//          `<Name>Schema` only — types NOT exported.
//          Derive at use site:
//            • Sagas:      SagaReturnType<typeof bankFeedApi.X>
//            • Components: Awaited<ReturnType<typeof bankFeedApi.X>>
// ============================================================================

// === Tier 1: Enums ==========================================================

export const ConnectionStatusSchema = z.enum([
  'ACTIVE',
  'INACTIVE',
  'FETCHING',
  'CONNECTED',
  'COMPLETED',
  'ERROR',
  'EXPIRED',
  // Waiting for the customer to sign in to the bank again; `relogin` says how.
  'LOGIN_REQUIRED',
  'FETCHING_ERROR',
  'REJECTED',
  'PARTIALLY_AUTHORIZED',
  // DPM-670 delegation statuses (backend-confirmed).
  'PENDING_CONNECTION',
  'REQUEST_EXPIRED',
  'REPLACED',
  'REVOKED',
  'SUSPENDED',
  'SUSPENDED_BY_PROVIDER',
  'TERMINATED_BY_USER',
]);
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>;

export const AccountStatusSchema = z.enum(['enabled', 'deleted', 'blocked']);
export type AccountStatus = z.infer<typeof AccountStatusSchema>;

export const ConnectionActionStateSchema = z.enum(['ENABLED', 'DISABLED', 'HIDDEN']);
export type ConnectionActionState = z.infer<typeof ConnectionActionStateSchema>;

export const SyncStatusSchema = z.enum(['PENDING', 'SYNCED', 'FAILED', 'RUNNING']).nullable();
export type SyncStatus = z.infer<typeof SyncStatusSchema>;

export const ConnectionTypeSchema = z.enum(['CORPORATE', 'PRIVATE']);
export type ConnectionType = z.infer<typeof ConnectionTypeSchema>;

// DPM-670: delivery channel of a delegated consent authorization request.
export const DelegationChannelSchema = z.enum(['EMAIL', 'WHATSAPP']);
export type DelegationChannel = z.infer<typeof DelegationChannelSchema>;

export const BankFeedAccountTypeSchema = z.enum(['CHECKING', 'CARD']);
export type BankFeedAccountType = z.infer<typeof BankFeedAccountTypeSchema>;

// Per-tab summary state (DA-19311). Single signal that replaces the earlier
// connection_exists / transaction_exists booleans: it selects the pre-data empty
// state (NO_CONNECTIONS / NO_TRANSACTIONS / CONNECTING) vs mounting the grid
// (CONNECTED). CONNECTING covers the window after a connection finishes setup
// while its transactions are still syncing (may take a few minutes).
export const TransactionSummaryStateSchema = z.enum(['CONNECTED', 'CONNECTING', 'NO_TRANSACTIONS', 'NO_CONNECTIONS']);
export type TransactionSummaryState = z.infer<typeof TransactionSummaryStateSchema>;

// === Tier 2: Entities =======================================================

export const BankFeedConnectionBankSchema = z.object({
  id: z.string(),
  name: z.string(),
  // Null for a bank with no stored logo — `.optional()` alone accepts undefined
  // but not the null the backend actually sends.
  picture: z.string().nullish(),
  // Null for a connection made by signing in directly rather than by consent:
  // there is no consent, so there is nothing to expire.
  consent_valid_until: z.string().nullable(),
});
export type BankFeedConnectionBank = z.infer<typeof BankFeedConnectionBankSchema>;

export const LastSyncValueSchema = z.object({
  status: SyncStatusSchema,
  synced_at: z.string().nullable(),
  error: z.string().nullable(),
});
export type LastSyncValue = z.infer<typeof LastSyncValueSchema>;

// The bank side of the last run only; sending what was read on to the ERP is `last_sync`.
export const ConnectionRefreshSchema = z.object({
  status: z.enum(['REFRESHED', 'RUNNING', 'FAILED', 'ACTION_REQUIRED', 'NOT_REFRESHED']),
  fetched_at: z.string().nullable(),
  error: z.string().nullable(),
});
export type ConnectionRefresh = z.infer<typeof ConnectionRefreshSchema>;

export const ErpAccountValueSchema = z.object({
  id: z.string(),
  name: z.string(),
  linked: z.boolean(),
  currency: z.string(),
});
export type ErpAccountValue = z.infer<typeof ErpAccountValueSchema>;

export const AccountMetaSchema = z.object({
  erp_account: z.object({ readonly: z.boolean() }),
});
export type AccountMeta = z.infer<typeof AccountMetaSchema>;

export const ConnectionActionPermissionsSchema = z.object({
  suspend: ConnectionActionStateSchema,
  unsuspend: ConnectionActionStateSchema,
  sync: ConnectionActionStateSchema,
  delete: ConnectionActionStateSchema,
  // Gates the "Resend authorization link" row action (backend-confirmed name).
  // Optional so rows from an older backend still parse; absent renders HIDDEN.
  resend: ConnectionActionStateSchema.optional(),
  // Signing in again on a LOGIN_REQUIRED connection; hidden for a login the bank blocked.
  repair: ConnectionActionStateSchema.optional(),
  // Only a working bank-login connection has accounts that can be managed.
  manage_accounts: ConnectionActionStateSchema.optional(),
});
export type ConnectionActionPermissions = z.infer<typeof ConnectionActionPermissionsSchema>;

// The previously submitted delegation request, carried on the connection row
// (null on non-delegated rows) so the resend dialog can prefill and replay it.
export const ConnectionDelegationSchema = z.object({
  connection_type: ConnectionTypeSchema,
  time_period: z.string(),
  corporate_id_number: z.string().nullable(),
  signatory_name: z.string(),
  channel: DelegationChannelSchema,
  // Exactly one of the two is set — the contact matching the chosen channel.
  signatory_email: z.string().nullable(),
  // `${dialCode}-${number}` — one dash-separated value.
  signatory_phone: z.string().nullable(),
});
export type ConnectionDelegation = z.infer<typeof ConnectionDelegationSchema>;

// Connection delegation (DPM-670): one write creates the connection and sends
// the authorization request. Backend-confirmed contract; also used by resend.
export const ConnectionDelegationBodySchema = ConnectionDelegationSchema.extend({
  provider_id: z.string(),
  // DPM-714: set only when resending — the id of the connection row carrying
  // the expired delegation request (a `BankFeedConnection.id`, not a delegation
  // id of its own), so the backend supersedes that row instead of adding a
  // second one. Omitted when the Connect flow creates a new delegation request.
  resend_connection_id: z.string().optional(),
});

// The authorization link is deliberately not returned; the response carries
// nothing the frontend needs (the new row arrives over WebSocket events).
export const ConnectionDelegationResponseSchema = z.unknown();

// On a connection account row the embedded ERP account is a thin reference
// ({ id, name }); the full ErpAccountValue (with linked, currency) is returned
// only by the bank-feed/erp-accounts list endpoint.
export const ErpAccountRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const BankFeedConnectionAccountSchema = z.object({
  account_number: z.string(),
  account_type: z.string(),
  // Open Finance legitimately returns null balance/balance_date (e.g. CARD
  // accounts with no transactions yet) and an empty-string account status
  // (e.g. CHECKING accounts) — the connections grid must accept both. Empty
  // status renders as a blank cell via the AccountStatusCellRenderer guard.
  balance: z.string().nullable(),
  balance_date: z.string().nullable(),
  connection_id: z.string(),
  currency: z.string(),
  erp_account: ErpAccountRefSchema.nullable(),
  id: z.string(),
  last_sync: LastSyncValueSchema.nullable(),
  meta: AccountMetaSchema,
  status: AccountStatusSchema.or(z.literal('')).nullable(),
  transaction_count: z.number(),
  uid: z.string(),
});
export type BankFeedConnectionAccount = z.infer<typeof BankFeedConnectionAccountSchema>;

export const BankFeedConnectionSchema = z.object({
  account_count: z.number(),
  accounts: z.array(BankFeedConnectionAccountSchema),
  actions: ConnectionActionPermissionsSchema,
  bank: BankFeedConnectionBankSchema,
  // Request/response asymmetry: frontend SENDS `'CORPORATE' | 'PRIVATE'`
  // (request body uses `ConnectionTypeSchema`), but backend transforms the
  // stored value to a user-friendly capitalized format on response (e.g.
  // 'Corporate'). No frontend consumer currently reads this field from the
  // grid/list response — grid renderers / cell renderers ignore it, only the
  // create-dialog Step1 sets it on submit. Keeping it as a plain string
  // avoids false drift catches while preserving the strict enum on the
  // outbound request side.
  connection_type: z.string(),
  creator: z.string(),
  id: z.string(),
  last_sync: LastSyncValueSchema,
  // Optional so rows from an older backend still parse.
  refresh: ConnectionRefreshSchema.nullable().optional(),
  status: ConnectionStatusSchema,
  // Optional so rows from an older backend still parse (null on non-delegated rows).
  delegation: ConnectionDelegationSchema.nullable().optional(),
  // Connected by signing in here rather than by consent redirect, so re-authorizing
  // it reopens the login dialog instead of sending the user to the bank.
  direct_login: z.boolean().optional().default(false),
  // Which sign-in this connection is waiting for. `session` needs nothing but the
  // person, `credentials` needs a password the bank now demands, `blocked` needs the
  // bank itself. Null when it is not waiting for one.
  relogin: z.enum(['session', 'credentials', 'blocked']).nullable().optional().default(null),
});
export type BankFeedConnection = z.infer<typeof BankFeedConnectionSchema>;

export const BankProviderSchema = z.object({
  id: z.string(),
  name: z.string(),
  // Null, not absent, for a bank that has no logo -- which is every bank reached by
  // signing in. `.optional()` alone rejects null and fails the whole response, so the
  // picker comes back empty and no bank can be chosen at all.
  picture: z.string().nullable().optional(),
  bank_code: z.string().nullable(),
  provider_type: z.string(),
  allow_connect: z.boolean(),
  // The same bank's business login, chosen with a toggle under the bank rather than
  // listed as a bank of its own.
  business_provider_id: z.string().nullable().optional(),
});
export type BankProvider = z.infer<typeof BankProviderSchema>;

// DPM-719. The backend owns the cadence vocabulary, its labels and their localisation; the two blocks
// can therefore offer different sets without the client knowing why.
export const SyncFrequencyOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  time_required: z.boolean(),
});
export type SyncFrequencyOption = z.infer<typeof SyncFrequencyOptionSchema>;

// Both sync blocks share this shape. Every field is modelled, server-owned ones included: the save is
// a full replace, and a field missing here is stripped on parse and then absent from the request.
export const BankFeedSyncItemSchema = z
  .object({
    enabled: z.boolean(),
    frequency: z.string(),
    hours: z.number().int().min(0).max(23),
    last_synced_at: z.string().nullable().optional(),
    // Required: an absent list would render an empty dropdown, which fails silently. Values must be
    // unique within a block: the row uses `value` as both the React key and the select identity, and
    // derives `time_required` with `.find()`, so a duplicate renders indistinguishable choices and
    // silently honours only the first. Same reasoning as the account-id rule on
    // `BankFeedSummaryItemSchema` below.
    frequency_options: z
      .array(SyncFrequencyOptionSchema)
      .refine((options) => new Set(options.map((option) => option.value)).size === options.length, {
        message: 'frequency_options: cadence values must be unique',
      }),
  })
  // `frequency` is only meaningful against the list shipped with it. A block where the two disagree
  // renders an empty dropdown next to a summary naming the missing value, so it is refused here like
  // every other violation of this contract rather than degrading in the UI.
  .refine((block) => block.frequency_options.some((option) => option.value === block.frequency), {
    path: ['frequency'],
    message: 'frequency must be one of frequency_options',
  });
export type BankFeedSyncItem = z.infer<typeof BankFeedSyncItemSchema>;

export const BankFeedSyncSettingsSchema = z.object({
  auto_sync: BankFeedSyncItemSchema,
  // null: card sync is unavailable to this company, so the section is not rendered. The backend alone
  // decides this; the client never inspects the ERP or the account mix.
  card_auto_sync: BankFeedSyncItemSchema.nullable().optional(),
});
export type BankFeedSyncSettings = z.infer<typeof BankFeedSyncSettingsSchema>;

// --- Transactions grid view ------------------------------------------------
// `BankFeedTransactionAccountSchema` is the per-transaction account embed (each grid row) — the
// unified `account` column that replaced the separate bank + account/card-number columns (DA-19447).
// `provider_id` is the provider (renamed from the old `bank.id`); `name` is the combined
// "<provider> - <number>" label (CHECKING: display account number, CARD: last 4 digits; unknown
// provider: just the number). Distinct from `BankFeedConnectionBankSchema` (per-connection bank
// with consent_valid_until).

export const BankFeedTransactionAccountSchema = z.object({
  provider_id: z.string(),
  name: z.string(),
  picture: z.string().nullable(),
  bank_code: z.string().nullable(),
});
export type BankFeedTransactionAccount = z.infer<typeof BankFeedTransactionAccountSchema>;

// Per-transaction status embed: backend sends `{ id, label, hint }` (id = raw status enum, label =
// BE-provided display text, hint = BE-provided tooltip text). Optional `hint` keeps rows from an
// older backend parsing (DPM-915).
export const BankFeedTransactionStatusSchema = z.object({
  id: z.string(),
  label: z.string(),
  hint: z.string().nullish(),
});
export type BankFeedTransactionStatus = z.infer<typeof BankFeedTransactionStatusSchema>;

// Row keys stay snake_case — AG Grid colDefs come from BE with snake_case `field`
// and read directly off the parsed row; transforming here would desync field
// matching. Contrast with `BankFeedSummarySchema` below which IS transformed to
// camelCase because it has no AG Grid field-binding constraint.
//
// Shared between the bank-transactions grid and the card-transactions grid: BE
// returns the same payload shape to both endpoints, and each grid renders the
// subset it needs through its own AG Grid colDefs (from the headers endpoint).
//
// Schema shape = "declare what we depend on, pass through the rest":
//   • `id` — row identity (AG Grid TData key); always present.
//   • `account` / `status` — the only STRUCTURED columns (objects, not string cells).
//     Declared explicitly so their nested shape is validated at runtime and typed;
//     `.nullish()` so the BE can drop the column without breaking the row parse.
//   • `.catchall(...)` — every OTHER column is a flat scalar cell, fully BE-driven
//     via the headers endpoint. We never read these by name (a project-wide grep
//     finds no `row.amount`/`row.debit`/etc.; AG Grid reads them dynamically via
//     `row[field]` and renderers via `params.value`). The catchall tolerates a
//     string OR a numeric value (int/float — amounts ship as strings today, but a
//     number must not break the parse) and normalises it to a string, so the BE can
//     add ANY new plain column with zero FE schema change while downstream still
//     sees the `string|null` contract the renderers + sum panel rely on. A strict
//     per-field list (the previous shape) broke the whole row parse when a declared
//     column was removed; a string-only catchall broke it when a numeric column was
//     added (DA-18943).
export const BankFeedTransactionRowSchema = z
  .object({
    id: z.string(),
    account: BankFeedTransactionAccountSchema.nullish(), // unified provider+number embed (DA-19447); see also connection's BankFeedConnectionBankSchema
    status: BankFeedTransactionStatusSchema.nullish(),
  })
  .catchall(
    z
      .union([z.string(), z.number()])
      .nullable()
      .transform((v) => (v == null ? v : String(v))),
  );
export type BankFeedTransactionRow = z.infer<typeof BankFeedTransactionRowSchema>;

// Per-account option for the toolbar "Bank and account" multiselect filter.
// `id`/`label`/`picture` are already camel-shaped on the wire (no underscores),
// so no key transform is needed inside the item transform below — passed through
// as-is. `picture` is the bank/card avatar URL (null when the BE has none).
// `currencies` (DA-19365) is the per-account currency list the toolbar Currency filter derives its
// options from — scoped by the "Bank and account" selection (see deriveCurrencyOptions); the
// tab-level `currencies` below is no longer the filter's source.
export const BankFeedSummaryAccountSchema = z.object({
  id: z.string(),
  label: z.string(),
  picture: z.string().nullable(),
  currencies: z.array(z.string()),
});
export type BankFeedSummaryAccount = z.infer<typeof BankFeedSummaryAccountSchema>;

// Summary schemas transform snake_case → camelCase at the parse boundary. Safe
// here because there's no AG Grid field-binding constraint (contrast with
// BankFeedTransactionRowSchema above): consumers are plain React components
// reading typed properties, so camelCase follows the project's naming convention
// (snake reserved for identifiers that mirror BE keys).
export const BankFeedSummaryItemSchema = z
  .object({
    account_type: z.string(),
    // id must be unique within a tab's account list: the toolbar "Bank and account" filter
    // uses id as the selection identity (getOptionKey / isOptionEqualToValue), so a duplicate
    // id would silently corrupt selection (one toggle ticks two rows). Fail loudly here.
    accounts: z
      .array(BankFeedSummaryAccountSchema)
      .refine((accounts) => new Set(accounts.map((a) => a.id)).size === accounts.length, {
        message: 'BankFeedSummary: account ids must be unique',
      }),
    // `state` is the single per-tab empty/loading/data signal (DA-19311, replaces the
    // earlier connection_exists / transaction_exists booleans). It drives the pre-data
    // empty-state selection (NO_CONNECTIONS / NO_TRANSACTIONS / CONNECTING) vs mounting
    // the grid (CONNECTED). transaction_count is the total (all-transactions) count for the
    // tab — it is company-wide and intentionally filter-independent (grid filters/search/date
    // range never change it), so the tab label always reflects every transaction (DA-19506).
    state: TransactionSummaryStateSchema,
    transaction_count: z.number(),
  })
  .transform((raw) => ({
    accountType: raw.account_type,
    accounts: raw.accounts,
    state: raw.state,
    transactionCount: raw.transaction_count,
  }));
export type BankFeedSummaryItem = z.infer<typeof BankFeedSummaryItemSchema>;

export const BankFeedSummarySchema = z
  .object({
    items: z.array(BankFeedSummaryItemSchema),
    currency: z.string(),
    // last_synced_at / sync_in_progress belong to the sync feature (tasks §9) and are not yet
    // emitted by the backend — deferred to that phase (field names may still change). See Q-B-8.
    export_in_progress: z.boolean(),
  })
  .transform((raw) => ({
    items: raw.items,
    defaultCurrency: raw.currency,
    exportInProgress: raw.export_in_progress,
  }));
export type BankFeedSummary = z.infer<typeof BankFeedSummarySchema>;

// --- User permissions ------------------------------------------------------

export const BankFeedUserPermissionsSchema = z.object({
  connection_manage: z.boolean(),
  connection_view: z.boolean(),
  transaction_sync: z.boolean(),
  transaction_view: z.boolean(),
});

// Settings user permissions grid row: only `user.id` is statically required (used as the
// merge key in cache updates); the rest (display name, boolean permission keys
// produced by the grid backend) is dynamic and stays opaque to TypeScript.
export const BankFeedSettingsUserPermissionRowSchema = z
  .object({
    user: z.object({ id: z.string() }).passthrough(),
  })
  .passthrough();
export type BankFeedSettingsUserPermissionRow = z.infer<typeof BankFeedSettingsUserPermissionRowSchema>;

export const BankFeedAutocompleteOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export type BankFeedAutocompleteOption = z.infer<typeof BankFeedAutocompleteOptionSchema>;

// Transactions grid — set-filter model emitted by BankFeedSetColumnFilter, parsed
// by ActiveFilterChips/formatFilterValue, and (when cascade ships) the per-column
// shape inside `filter_model` of the autocomplete request.
export const BankFeedSetFilterModelSchema = z.object({
  filterType: z.literal('set'),
  type: z.enum(['contains', 'notContains']),
  values: z.array(BankFeedAutocompleteOptionSchema),
});
export type BankFeedSetFilterModel = z.infer<typeof BankFeedSetFilterModelSchema>;

// === Tier 3: Per-endpoint schemas ===========================================
// No type exports — derive via SagaReturnType / Awaited<ReturnType<>> at use site.

// Shared paths (reused across multiple endpoints)
export const ConnectionIdPathSchema = z.object({ id: z.string() });

// Connections grid (narrows ClientSideGridResponse items)
export const ConnectionsGridResponseSchema = ClientSideGridResponseSchema.extend({
  items: z.array(BankFeedConnectionSchema),
});

// Settings user permissions grid (items narrowed via BankFeedSettingsUserPermissionRowSchema)
export const BankFeedSettingsUserPermissionsGridResponseSchema = ClientSideGridResponseSchema.extend({
  items: z.array(BankFeedSettingsUserPermissionRowSchema),
});

// Providers
export const BankProvidersResponseSchema = z.object({
  items: z.array(BankProviderSchema),
});

// ERP accounts (list)
export const ErpAccountsResponseSchema = z.object({
  items: z.array(ErpAccountValueSchema),
});

// ERP accounts (match)
export const ErpAccountsMatchBodySchema = z.object({
  account_id: z.string(),
  connection_id: z.string(),
  erp_account_id: z.string().nullable(),
  currency: z.string(),
});

// Connections by IDs (lookup)
export const ConnectionsByIdsBodySchema = z.object({
  connection_ids: z.array(z.string()),
});
export const ConnectionsByIdsResponseSchema = z.object({
  items: z.array(BankFeedConnectionSchema),
});

// Connection create
export const ConnectionCreateBodySchema = z.object({
  provider_id: z.string(),
  connection_type: ConnectionTypeSchema,
  user_id_type: z.string(),
  user_id_number: z.string(),
  time_period: z.string(),
  corporate_id_number: z.string().nullable(),
});
export const ConnectionCreateResponseSchema = z.object({
  connection_url: z.string(),
  connection: BankFeedConnectionSchema,
});

// Connection limit (DPM-829), backend-confirmed.
export const ConnectionsLimitResponseSchema = z.object({
  // null = no limit for the company
  limit: z.number().nullable(),
  used: z.number(),
  can_connect: z.boolean(),
  // How this company may add connections, primary first: `open_finance`, or `legacy_banking`
  // for an organization that connects its banks by signing in to them.
  integrations: z.array(z.string()).optional().default([]),
});

// Freshdesk ticket via the AP `createTicket` endpoint (DPM-830 "Request more
// connections"). Payload is ours until the backend defines one.
export const SupportTicketBodySchema = z.object({
  subject: z.string(),
  description: z.string(),
  custom_fields: z.object({
    cf_app_url: z.string(),
    cf_product: z.string(),
  }),
});

// Connection update (PATCH body)
export const ConnectionUpdateBodySchema = z.object({
  suspended: z.boolean().optional(),
});

// Settings user permissions update (body shape: { user_id: string } & Record<string, boolean>)
export const BankFeedSettingsUserPermissionsUpdateBodySchema = z.object({ user_id: z.string() }).catchall(z.boolean());
export const BankFeedSettingsUserPermissionsUpdateResponseSchema = z.object({
  items: z.array(BankFeedSettingsUserPermissionRowSchema),
});

// Transactions grid — autocomplete options for set-filtered columns.
// `filter_model` is pass-through of `gridApi.getFilterModel()` (mixed filter
// types per column); kept loose at the boundary, BE owns semantic validation.
export const TransactionsAutocompleteBodySchema = z.object({
  field: z.string(),
  account_type: BankFeedAccountTypeSchema,
  // Mirrors the grid-search "Bank and account" filter so column-filter option
  // lists reflect the same account scope. Empty array = all accounts.
  account_ids: z.array(z.string()),
  currency: z.string(),
  filter_model: z.record(z.string(), z.unknown()).optional(),
});
export const TransactionsAutocompleteResponseSchema = z.object({
  options: z.array(BankFeedAutocompleteOptionSchema),
});

// Transactions grid — cursor-paginated rows.
// Cursor tokens (`search_after` / `search_before`) are opaque BE continuations;
// FE round-trips them in subsequent requests but does not interpret them.
export const BankFeedTransactionsGridResponseSchema = z.object({
  items: z.array(BankFeedTransactionRowSchema),
  count: z.number(),
  search_after: z.string().nullable(),
  search_before: z.string().nullable(),
});

// Wraps a base ZodObject into the date-period union, encoding the invariant that
// the named windows (`last_30d` / `last_90d` / `last_12m`) carry no bounds (BE
// resolves them itself), while `custom` carries EITHER a non-empty set of whole
// days (CARD payment-date filter, DPM-667) OR explicit `date_from` / `date_to` —
// never both forms, never neither. Flat-with-optional would admit invalid combos
// like `last_30d + date_from` or a bare `custom`; the branch shapes keep those
// unrepresentable in the derived input type, not merely refused at runtime.
//
// A plain `union`, NOT `discriminatedUnion`: two branches share the discriminator
// value `custom`, which zod rejects outright ("Duplicate discriminator value").
// Verified on zod 4.3.6 — this union strips a stray `date_from` off a `dates`
// body, and rejects both an empty `dates` and a `custom` carrying neither form.
// Cost: union parse errors are less pointed than a discriminated union's; that is
// dev-facing only, since these bodies are built solely by buildTransactionsDate-
// Payload, so a failure is a frontend bug report, not user input feedback.
const withDatePeriod = <T extends z.ZodObject<z.ZodRawShape>>(base: T) =>
  z.union([
    base.extend({ date_period: z.enum(['last_30d', 'last_90d', 'last_12m']) }),
    base.extend({
      date_period: z.literal('custom'),
      dates: z.array(z.string()).nonempty(),
    }),
    base.extend({
      date_period: z.literal('custom'),
      date_from: z.string(),
      date_to: z.string(),
    }),
  ]);

// Transactions grid — search request body.
// `sort_model` / `filter_model` are pass-through of AG Grid's `request.sortModel`
// and `request.getFilterModel()` respectively; their inner shapes are AG Grid
// concerns and stay loose at this boundary (BE owns semantic validation).
const TransactionsGridSearchBodyBaseSchema = z.object({
  sort_model: z.array(z.object({ colId: z.string(), sort: z.enum(['asc', 'desc']) })),
  filter_model: z.record(z.string(), z.unknown()),
  account_type: BankFeedAccountTypeSchema,
  // Toolbar "Bank and account" filter. Empty array = all accounts (no filter);
  // a non-empty list narrows to those account ids. Inherited by the export body
  // (which omits only limit/cursors), so export honours the same selection.
  account_ids: z.array(z.string()),
  currency: z.string(),
  text: z.array(z.string()),
  limit: z.number(),
  search_after: z.string().nullable(),
  search_before: z.string().nullable(),
});
export const TransactionsGridSearchBodySchema = withDatePeriod(TransactionsGridSearchBodyBaseSchema);

// Transactions grid — column definitions (per accountType).
// `type` / `operator` are BE-controlled enums kept loose at the boundary —
// server adds new types as needed and a strict z.enum() would crash FE on
// rollout. `filterParams` / `headerComponentParams` / `cellRendererParams`
// are opaque pass-through bags consumed by AG Grid via normalizeAgGridColDefs.
export const TransactionsGridHeadersQuerySchema = z.object({
  account_type: BankFeedAccountTypeSchema,
});
export const TransactionsGridHeadersResponseSchema = z.object({
  headers: z.array(
    z.object({
      field: z.string(),
      headerName: z.string(),
      type: z.string(),
      operator: z.string(),
      hide: z.boolean(),
      editable: z.boolean(),
      sortable: z.boolean(),
      filter: z.boolean(),
      filterParams: z.record(z.string(), z.unknown()),
      headerComponentParams: z.record(z.string(), z.unknown()),
      cellRendererParams: z.record(z.string(), z.unknown()),
    }),
  ),
});

// Transactions grid — Excel/CSV export request body.
// Same filter envelope as TransactionsGridSearchBodySchema (sort/filter/account
// /currency/text + the same date-period union) minus pagination — export
// returns the full filtered set, so `limit` / `search_after` / `search_before`
// are not applicable.
//   • `headers` — explicit column subset to include. BE also accepts null
//     for "all visible columns"; FE always sends an explicit list (the grid
//     knows the visible set at click time), so the schema narrows to array.
//   • `file_format` — Phase 2 wiring sends only 'xlsx'; 'csv' is BE-side
//     reserved.
export const BankFeedTransactionsExportBaseSchema = TransactionsGridSearchBodyBaseSchema.omit({
  limit: true,
  search_after: true,
  search_before: true,
}).extend({
  headers: z.array(z.string()),
  file_format: z.enum(['csv', 'xlsx']),
});
export const BankFeedTransactionsExportBodySchema = withDatePeriod(BankFeedTransactionsExportBaseSchema);
export const BankFeedTransactionsExportResponseSchema = z.object({
  export_id: z.string(),
});

// Empirical: BE sends 'READY' on completion, 'FAILED' on terminal error,
// 'PENDING' while in-flight. Shared between the GET response and the
// TRANSACTION_EXPORT_COMPLETED WS event metadata.
export const BankFeedExportStatusSchema = z.enum(['PENDING', 'READY', 'FAILED']);
export type BankFeedExportStatus = z.infer<typeof BankFeedExportStatusSchema>;

// Transactions export status — GET bank-feed/exports/{export_id}. Discriminated
// on `status` so that "READY without file_url" (a BE invariant violation) and
// other illegal field combinations fail at the Zod boundary instead of silently
// falling through saga branches. `.passthrough()` per branch keeps unknown
// sibling fields (e.g. created_at, progress) tolerated.
export const BankFeedExportPathSchema = z.object({ exportId: z.string() });
export const BankFeedExportStatusResponseSchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('READY'),
      file_url: z.string(),
      error_message: z.string().nullable().optional(),
    })
    .passthrough(),
  z
    .object({
      status: z.literal('FAILED'),
      file_url: z.string().optional(),
      error_message: z.string().nullable(),
    })
    .passthrough(),
  z
    .object({
      status: z.literal('PENDING'),
      file_url: z.string().optional(),
      error_message: z.string().nullable().optional(),
    })
    .passthrough(),
]);

// WS event metadata for TRANSACTION_EXPORT_COMPLETED. Boundary-validated
// in src/domain/ws/handlers.js before dispatching the saga action.
export const BankFeedTransactionExportEventMetadataSchema = z.object({
  export_id: z.string(),
  status: BankFeedExportStatusSchema,
  error_message: z.string().nullable(),
});
export type BankFeedTransactionExportEventMetadata = z.infer<typeof BankFeedTransactionExportEventMetadataSchema>;

// WS event metadata for BANK_CONNECTION_SETUP_COMPLETED (page bank_feed_transactions).
// Boundary-validated in src/domain/ws/handlers.js before dispatching the saga action.
// `success` — the sole connection finished setup: true → became ACTIVE (data fetched),
// false → ended in ERROR. Either way the transactions page re-fetches its summary.
export const BankFeedConnectionSetupCompletedMetadataSchema = z.object({
  success: z.boolean(),
});
export type BankFeedConnectionSetupCompletedMetadata = z.infer<typeof BankFeedConnectionSetupCompletedMetadataSchema>;

// ── Direct bank login (legacy banking connections) ────────────────────────────────
// Signing in on the user's behalf can be interrupted by the bank texting them a
// code, so enrollment is a small state machine rather than one call: create,
// then poll, then answer if asked.

export const LegacyBankingConnectionStateSchema = z.object({
  connection_id: z.string(),
  status: z.string(),
  otp_required: z.boolean(),
  // Describes the prompt to render — where the code was sent, and whether it is
  // one field or several. The banks differ, so the UI is told rather than assuming.
  otp_context: z
    .object({
      bank: z.string().optional(),
      portal: z.string().optional(),
      delivery: z.string().optional(),
      code_field: z.string().optional(),
    })
    .nullable()
    .optional(),
  error: z.string().nullable().optional(),
  accounts_pending: z.boolean().optional().default(false),
});
export type LegacyBankingConnectionState = z.infer<typeof LegacyBankingConnectionStateSchema>;

// The sign-in found accounts and stopped, waiting for them to be assigned. Needed
// because a parked enrolment looks exactly like one still in progress otherwise.
export const LegacyBankingDiscoveredAccountSchema = z.object({
  account_number: z.string(),
  // What the bank calls the account. Absent for banks that do not name accounts.
  account_name: z.string().nullable().optional(),
  account_type: z.string(),
  // On cards only: bankIssued, or companyIssued for a card the bank did not issue.
  card_type: z.string().nullable().optional(),
  currency: z.string(),
  balance: z.number().nullable().optional(),
});
export type LegacyBankingDiscoveredAccount = z.infer<typeof LegacyBankingDiscoveredAccountSchema>;
export const LegacyBankingDiscoveredAccountsResponseSchema = z.array(LegacyBankingDiscoveredAccountSchema);

// Every account the login can see, with the company already holding each one. Read
// from what the last pull stored, so opening the picker costs no bank visit.
export const LegacyBankingLoginAccountSchema = LegacyBankingDiscoveredAccountSchema.extend({
  connected_to: z.string().nullable().optional().default(null),
});
export type LegacyBankingLoginAccount = z.infer<typeof LegacyBankingLoginAccountSchema>;
export const LegacyBankingLoginAccountsResponseSchema = z.array(LegacyBankingLoginAccountSchema);

export const LegacyBankingLinkableCompanySchema = z.object({
  id: z.string(),
  name: z.string(),
  // How full the company already is. One at its limit still accepts the account —
  // the connection it gets is created suspended rather than refused.
  connections_used: z.number().optional().default(0),
  connection_limit: z.number().optional().default(0),
});
export type LegacyBankingLinkableCompany = z.infer<typeof LegacyBankingLinkableCompanySchema>;
export const LegacyBankingLinkableCompaniesResponseSchema = z.array(LegacyBankingLinkableCompanySchema);

// Account number to company id. The server checks every company again, so this is
// what the user picked, not what they are allowed to pick.
export const LegacyBankingAssignAccountsBodySchema = z.object({
  assignments: z.record(z.string(), z.string()),
});
export const LegacyBankingAssignAccountsResponseSchema = z.array(LegacyBankingConnectionStateSchema);

export const LegacyBankingConnectionCreateBodySchema = z.object({
  provider_id: z.string(),
  credentials: z.record(z.string(), z.string()),
});

export const LegacyBankingOtpBodySchema = z.object({
  code: z.string().min(1),
});

// Omitted for the ordinary sign-in, where the saved password still works. Sent only
// when the bank refused it, and then it replaces the one held for every company
// reading through that login.
export const LegacyBankingReauthBodySchema = z.object({
  credentials: z.record(z.string(), z.string()).optional(),
});
